import { useCallback, useEffect, useImperativeHandle, useMemo } from 'react';

import { FlexAligner, SelectOption } from '@scaleaq/scaleui';
import { InventoryProvider } from '@scaleaq/sitecontrol-lib';
import { DateTime, Duration } from 'luxon';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { AddButton } from '@/components/AddButton.tsx';
import { FieldArrayItem } from '@/components/fieldArrayItem';
import {
  ControlledDateField,
  ControlledRadioField,
  ControlledSelectField,
  ControlledTextOrNumberField,
} from '@/components/inputFields';
import { useClassInventoryProviderPair } from '@/hooks';
import { responsive } from '@/utils';

import { useLiveChartOptions, useSensorsAndProviders } from '../../hooks.ts';
import {
  BoxPlotCategory,
  BoxPlotChartConfigType,
  ChartConfigProps,
  ChartInterval,
  ChartType,
  PlotType,
} from '../../types.ts';

export const BoxPlotChartConfig = ({
  config,
  onSave,
  id,
  ref,
  rootId,
}: ChartConfigProps<BoxPlotChartConfigType>) => {
  const { t } = useTranslation(['chartConfig', 'common']);
  const { classInventoryProviderPair } = useClassInventoryProviderPair();

  const {
    control,
    getValues,
    trigger,
    setValue,
    formState: { errors },
  } = useForm<BoxPlotChartConfigType>({
    defaultValues: {
      ...config,
      plotType: PlotType.BOXPLOT,
    },
  });

  const { fields, append, remove } = useFieldArray({
    name: 'boxes',
    control,
  });

  const categoryWatcher = useWatch({ control, name: 'category' });
  const providerWatcher = useWatch({ control, name: `boxes.${0}.provider` });
  const sensorIdWatcher = useWatch({ control, name: `boxes.${0}.sensorId` });
  const startDateWatcher = useWatch({ control, name: `boxes.${0}.start` });
  const endDateWatcher = useWatch({ control, name: `boxes.${0}.end` });
  const boxWatcher = useWatch({ control, name: 'boxes' });
  const chartTypeWatcher = useWatch({ control, name: 'type' });
  const intervalWatcher = useWatch({ control, name: 'interval' });

  const selectedSensorIds = useMemo(() => {
    const sensorSet = new Set<string>();
    boxWatcher?.forEach(box => {
      if (box.sensorId) sensorSet.add(box.sensorId);
    });
    return Array.from(sensorSet);
  }, [boxWatcher]);

  const selectedProviders = useMemo(() => {
    const providerSet = new Set<InventoryProvider>();
    boxWatcher?.forEach(box => providerSet.add(classInventoryProviderPair[box.provider]));
    return Array.from(providerSet).filter(
      (provider): provider is InventoryProvider => provider !== undefined
    );
  }, [boxWatcher, classInventoryProviderPair]);

  const categoryOptions: SelectOption[] = [
    {
      id: uuidv4(),
      label: t('chartConfig:boxPlot.category.day'),
      value: BoxPlotCategory.Day,
    },
    {
      id: uuidv4(),
      label: t('chartConfig:boxPlot.category.sensor'),
      value: BoxPlotCategory.Sensor,
    },
    {
      id: uuidv4(),
      label: t('chartConfig:boxPlot.category.provider'),
      value: BoxPlotCategory.Provider,
    },
  ];

  const { chartTypeOptions, chartIntervalOptions } = useLiveChartOptions({
    minInterval: categoryWatcher === BoxPlotCategory.Day ? ChartInterval.Day : undefined,
    maxInterval: categoryWatcher === BoxPlotCategory.Day ? ChartInterval.Month : undefined,
  });

  // reset interval whenever intervalOptions changes, to prevent invalid interval values from being used
  useEffect(() => {
    if (!chartIntervalOptions.map(opt => opt.value).includes(intervalWatcher))
      setValue('interval', chartIntervalOptions[0].value);
  }, [chartIntervalOptions, intervalWatcher, setValue]);

  const { sensorOptions, providerOptions, isLoading } = useSensorsAndProviders(
    selectedSensorIds,
    selectedProviders,
    rootId
  );

  const isOneSensor = useMemo(
    () => !!categoryWatcher && categoryWatcher !== BoxPlotCategory.Sensor,
    [categoryWatcher]
  );
  const isOneProvider = useMemo(
    () => !!categoryWatcher && categoryWatcher !== BoxPlotCategory.Provider,
    [categoryWatcher]
  );
  const isOneDay = useMemo(
    () => !!categoryWatcher && categoryWatcher !== BoxPlotCategory.Day,
    [categoryWatcher]
  );
  const showBoxes = useMemo(
    () =>
      chartTypeWatcher &&
      !(categoryWatcher === BoxPlotCategory.Day && chartTypeWatcher === ChartType.LIVE),
    [categoryWatcher, chartTypeWatcher]
  );

  // Call to ensure all common properties are properly set.
  const updateBoxes = useCallback(() => {
    for (let i = 1; i < fields.length; i++) {
      switch (categoryWatcher) {
        case BoxPlotCategory.Day:
          setValue(`boxes.${i}.sensorId`, sensorIdWatcher);
          setValue(`boxes.${i}.provider`, providerWatcher);
          break;
        case BoxPlotCategory.Sensor:
          setValue(`boxes.${i}.provider`, providerWatcher);
          setValue(`boxes.${i}.start`, startDateWatcher);
          setValue(`boxes.${i}.end`, endDateWatcher);
          break;
        case BoxPlotCategory.Provider:
          setValue(`boxes.${i}.sensorId`, sensorIdWatcher);
          setValue(`boxes.${i}.start`, startDateWatcher);
          setValue(`boxes.${i}.end`, endDateWatcher);
      }
    }
  }, [
    categoryWatcher,
    endDateWatcher,
    fields.length,
    providerWatcher,
    sensorIdWatcher,
    setValue,
    startDateWatcher,
  ]);

  const triggerSave = async () => {
    updateBoxes();
    const isValid = await trigger();
    if (isValid) onSave(id, getValues());
  };

  useImperativeHandle(ref, () => ({
    save: () => triggerSave().catch(err => console.error(err)),
  }));

  return (
    <FlexAligner direction={'column'} gap={responsive.spacing['5xlarge']} fullWidth>
      <ControlledTextOrNumberField control={control} name={'title'} label={t('common:title')} />
      <ControlledSelectField
        name={'category'}
        control={control}
        label={t('chartConfig:boxPlot.label.category')}
        placeholder={t('chartConfig:boxPlot.categoryPlaceholder')}
        options={categoryOptions}
        onSelect={() => {
          if (!fields.length)
            append({
              sensorId: '',
              provider: '',
              start: DateTime.now().minus(Duration.fromObject({ days: 1 })),
              end: DateTime.now(),
            });
        }}
        rules={{
          required: t('chartConfig:boxPlot.isRequired.category'),
        }}
        caution={errors.category?.message}
      />

      <ControlledSelectField
        render={isOneSensor}
        label={t('chartConfig:chartConfigModal.fields.sensorToPlot')}
        placeholder={t('chartConfig:placeholder.sensor')}
        control={control}
        name={`boxes.${0}.sensorId`}
        rules={{
          required: t('chartConfig:boxPlot.isRequired.sensor'),
        }}
        caution={errors.boxes?.[0]?.sensorId?.message}
        options={sensorOptions}
        isLoading={isLoading}
        isClearable={true}
      />
      <ControlledSelectField
        render={isOneProvider}
        label={t('chartConfig:chartConfigModal.fields.typeToPlot')}
        placeholder={t('chartConfig:placeholder.type')}
        name={`boxes.${0}.provider`}
        control={control}
        options={providerOptions}
        rules={{
          required: t('chartConfig:boxPlot.isRequired.type'),
        }}
        caution={errors.boxes?.[0]?.provider?.message}
        isLoading={isLoading}
        isClearable={true}
      />

      <ControlledRadioField
        render={!!categoryWatcher}
        control={control}
        name={'type'}
        label={t('chartConfig:chartConfigModal.fields.chartType')}
        options={chartTypeOptions}
        rules={{
          required: t('chartConfig:chartConfigModal.rules.isRequired.chartType'),
        }}
        caution={errors.type?.message}
      />
      <ControlledRadioField
        render={chartTypeWatcher === ChartType.LIVE}
        control={control}
        name={'interval'}
        label={t('chartConfig:chartConfigModal.fields.interval')}
        options={chartIntervalOptions}
        rules={{
          required: t('chartConfig:chartConfigModal.rules.isRequired.interval'),
        }}
        caution={errors.interval?.message}
      />
      <ControlledDateField
        render={isOneDay && chartTypeWatcher === ChartType.HISTORICAL}
        control={control}
        name={`boxes.${0}.start`}
        label={t('chartConfig:chartConfigModal.fields.from')}
        rules={{
          required: t('chartConfig:boxPlot.isRequired.start'),
        }}
        caution={errors.boxes?.[0]?.start?.message}
      />
      <ControlledDateField
        render={isOneDay && chartTypeWatcher === ChartType.HISTORICAL}
        control={control}
        name={`boxes.${0}.end`}
        label={t('chartConfig:chartConfigModal.fields.to')}
        rules={{
          required: t('chartConfig:boxPlot.isRequired.end'),
        }}
        caution={errors.boxes?.[0]?.end?.message}
      />
      {!!categoryWatcher &&
        showBoxes &&
        fields.map((field, index) => (
          <FieldArrayItem
            title={t('chartConfig:boxPlot.label.box', { index: index + 1 })}
            key={field.id}
            onRemove={() => remove(index)}
            disableRemove={fields.length === 1}
          >
            <FlexAligner direction={'column'} gap={responsive.spacing['3xlarge']}>
              <ControlledSelectField
                render={!isOneSensor}
                label={t('chartConfig:boxPlot.label.sensor')}
                placeholder={t('chartConfig:placeholder.sensor')}
                control={control}
                name={`boxes.${index}.sensorId`}
                rules={{
                  required: t('chartConfig:boxPlot.isRequired.sensor'),
                }}
                caution={errors.boxes?.[index]?.sensorId?.message}
                options={sensorOptions}
                isLoading={isLoading}
              />

              <ControlledSelectField
                render={!isOneProvider}
                label={t('chartConfig:boxPlot.label.type')}
                placeholder={t('chartConfig:placeholder.type')}
                name={`boxes.${index}.provider`}
                control={control}
                options={providerOptions}
                rules={{
                  required: t('chartConfig:boxPlot.isRequired.type'),
                }}
                caution={errors.boxes?.[index]?.provider?.message}
                isLoading={isLoading}
              />

              <ControlledDateField
                render={!isOneDay}
                control={control}
                name={`boxes.${index}.start`}
                label={t('chartConfig:chartConfigModal.fields.from')}
                rules={{
                  required: t('chartConfig:boxPlot.isRequired.start'),
                }}
                caution={errors.boxes?.[index]?.start?.message}
              />
              <ControlledDateField
                render={!isOneDay}
                control={control}
                name={`boxes.${index}.end`}
                label={t('chartConfig:chartConfigModal.fields.to')}
                rules={{
                  required: t('chartConfig:boxPlot.isRequired.end'),
                }}
                caution={errors.boxes?.[index]?.end?.message}
              />
            </FlexAligner>
          </FieldArrayItem>
        ))}
      {showBoxes && (
        <AddButton
          onAdd={() => {
            const values = getValues();
            const previousStart = values.boxes?.[fields.length - 1]?.start;
            const previousEnd = values.boxes?.[fields.length - 1]?.end;
            append({
              sensorId: values.boxes?.[fields.length - 1]?.sensorId ?? '',
              provider: values.boxes?.[fields.length - 1]?.provider ?? '',
              start:
                categoryWatcher === BoxPlotCategory.Day
                  ? previousStart?.minus(Duration.fromObject({ days: 1 }))
                  : previousStart,
              end: categoryWatcher === BoxPlotCategory.Day ? previousStart : previousEnd,
            });
          }}
          label={t('boxPlot.addBoxButton')}
          render={!!categoryWatcher}
        />
      )}
    </FlexAligner>
  );
};
